import type {
  StartSignProcessDto,
  AccessTokenInfoDtoType,
  ProcessDtoType,
} from '@cencert/mob-signature'

export function getToken(): string | null {
  // First check sessionStorage
  const token = getTokenFromSession()
  return token
}

export function saveToken(token: string): void {
  saveTokenToSession(token)
}

export function clearToken(): void {
  clearTokenCookie()
}

export function getProcessId(): string | null {
  const sessionData = getSessionData()
  return sessionData?.processId || null
}

export function saveProcessId(id: string): void {
  const sessionData = getSessionData() || {}
  sessionData.processId = id
  saveSessionData(sessionData)
}

export function getTokenFromSession(): string | null {
  const sessionData = getSessionData()
  return sessionData?.token ?? null
}

export function saveTokenToSession(token: string): void {
  const sessionData = getSessionData() ?? {}
  sessionData.token = token
  saveSessionData(sessionData)
}

export function clearProcessId(): void {
  const sessionData = getSessionData()
  if (sessionData) {
    delete sessionData.processId
    saveSessionData(sessionData)
  }
}

export function getExpirationTime(): string | null {
  return sessionStorage.getItem('expirationTime')
}

export function saveExpirationTime(time: string): void {
  sessionStorage.setItem('expirationTime', time)
}

export function clearExpirationTime(): void {
  sessionStorage.removeItem('expirationTime')
}

export function getAvailableFreeSignatures(): boolean | null {
  const value = sessionStorage.getItem('availableFreeSignatures')
  return value !== null ? JSON.parse(value) : null
}

export function saveAvailableFreeSignatures(available: boolean): void {
  sessionStorage.setItem('availableFreeSignatures', JSON.stringify(available))
}

export function clearAvailableFreeSignatures(): void {
  sessionStorage.removeItem('availableFreeSignatures')
}

export function getAccessTokenType(): AccessTokenInfoDtoType | null {
  const value = sessionStorage.getItem('accessTokenType')
  return value !== null ? (value as AccessTokenInfoDtoType) : null
}

export function saveAccessTokenType(
  type: AccessTokenInfoDtoType | ProcessDtoType
): void {
  sessionStorage.setItem('accessTokenType', type)
}

export function clearAccessTokenType(): void {
  sessionStorage.removeItem('accessTokenType')
}

export function clearAllSessionData(): void {
  // Clear all session storage items
  clearProcessId()
  clearExpirationTime()
  clearAvailableFreeSignatures()
  clearAccessTokenType()
  clearAppSessionActive()
  clearSessionData()
  clearNavigationState()
  clearAppDetectionState()
  clearQrCodeData()
  clearErrorMessage()
}

export function clearAppSessionActive(): void {
  sessionStorage.removeItem('app_session_active')
}

export function getSessionData(): any | null {
  const value = sessionStorage.getItem('session_data')
  return value !== null ? JSON.parse(value) : null
}

export function saveSessionData(data: any): void {
  sessionStorage.setItem('session_data', JSON.stringify(data))
}

export function clearSessionData(): void {
  sessionStorage.removeItem('session_data')
}

export function clearNavigationState(): void {
  sessionStorage.removeItem('process_navigation_state')
}

export function isAuthenticated(): boolean {
  return getToken() !== null
}

export function getTokenFromCookie(): string | null {
  const cookies = document.cookie.split(';')
  console.log('All cookies:', document.cookie)
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === 'auth_token') {
      console.log(
        'Found auth_token:',
        value ? `${value.substring(0, 20)}...` : 'empty'
      )
      return value || null
    }
  }
  console.log('No auth_token found in cookies')
  return null
}

export function clearTokenCookie(): void {
  document.cookie = 'auth_token=; Max-Age=0; Path=/'
}

export function getTokenFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('token')
}

// App Detection State Management
export interface AppDetectionState {
  isRefreshedWithProcessId: boolean
}

export function getAppDetectionState(): AppDetectionState | null {
  const value = sessionStorage.getItem('app_detection_state')
  return value !== null ? JSON.parse(value) : null
}

export function saveAppDetectionState(state: AppDetectionState): void {
  sessionStorage.setItem('app_detection_state', JSON.stringify(state))
}

export function clearAppDetectionState(): void {
  sessionStorage.removeItem('app_detection_state')
}

// QR Code Data Management
export function getQrCodeData(): StartSignProcessDto | null {
  const value = sessionStorage.getItem('qr_code_data')
  if (value !== null) {
    try {
      return JSON.parse(value)
    } catch (e) {
      console.error('Failed to parse qr_code_data:', e)
      return null
    }
  }
  return null
}

export function saveQrCodeData(qrCode: StartSignProcessDto): void {
  sessionStorage.setItem('qr_code_data', JSON.stringify(qrCode))
}

export function clearQrCodeData(): void {
  sessionStorage.removeItem('qr_code_data')
}

export function saveMobileAppAvailable(available: boolean): void {
  sessionStorage.setItem('mobile_app_available', JSON.stringify(available))
}

export function getMobileAppAvailable(): boolean {
  const value = sessionStorage.getItem('mobile_app_available')
  return value !== null ? JSON.parse(value) : false
}

export function saveErrorMessage(message: string): void {
  sessionStorage.setItem('error_message', message)
}

export function getErrorMessage(): string | null {
  return sessionStorage.getItem('error_message')
}

export function clearErrorMessage(): void {
  sessionStorage.removeItem('error_message')
}

export function saveIsMObywatelProcess(isMObywatel: boolean): void {
  sessionStorage.setItem('is_mobywatel_process', JSON.stringify(isMObywatel))
}

export function getIsMObywatelProcess(): boolean {
  const value = sessionStorage.getItem('is_mobywatel_process')
  return value !== null ? JSON.parse(value) : false
}
function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function saveWebReturnUrl(url: string): void {
  if (!url || !isValidUrl(url)) return
  sessionStorage.setItem('web_return_url', url)
}

export function getWebReturnUrl(): string | null {
  return sessionStorage.getItem('web_return_url')
}
